import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { HttpParams } from "@angular/common/http";
import { ApiService } from "app/service/api.service";
import { API_ROUTE } from "app/api-routes";
import { RoleFormComponent } from "./role-form/role-form.component";
import { RoleDetailComponent } from "./role-detail/role-detail.component";
import { FuseConfirmationDialogComponent } from "@fuse/services/confirmation/dialog/dialog.component";
import {
  debounceTime,
  distinctUntilChanged,
  Subject,
  switchMap,
  tap,
} from "rxjs";
import { BreadcrumbComponent } from "app/layout/common/breadcrumb/breadcrumb.component";
import { MatButtonModule } from "@angular/material/button";
import { MatChipsModule } from "@angular/material/chips";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { BaseComponent } from "app/layout/base/base.component";
export interface Role {
  _id: string;
  name: string;
  desc: string;
  modularPermission: string[];
  functionalPermissionToAdd: string[];
  functionalPermissionToUpdate: string[];
  functionalPermissionToDelete: string[];
  otherFunctionalPermission: string[];
  functionalPermission: string[];
  metadata: any;
}

@Component({
  selector: "role",
  standalone: true,
  templateUrl: "./role.component.html",
  styleUrls: ["./role.component.scss"],
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    CommonModule,
    MatChipsModule,
    BreadcrumbComponent,
    FormsModule,
    ReactiveFormsModule,
    MatTooltipModule,
  ],
})
export class RoleComponent extends BaseComponent implements AfterViewInit {
  private _snackBar = inject(MatSnackBar);
  displayedColumns: string[] = [
    "serial",
    "name",
    "userRoleType",
    "desc",
    "createdAt",
    "updatedAt",
    "actions",
  ];
  dataSource: MatTableDataSource<Role> = new MatTableDataSource<Role>([]);
  @ViewChild(MatSort) sort!: MatSort;
  roles: Role[] = [];
  isLoading = false;
  searchForm: FormGroup;
  pagination = {
    length: 0,
    limit: 10,
    page: 0,
  };
  private searchSubject = new Subject<string>();

  constructor(
    private dialog: MatDialog,
    private _apiServices: ApiService,
    private fb: FormBuilder
  ) {
    super();
    this.initData();

    this.searchForm = this.fb.group({
      search: [""], // Input field for search
    });

    this.searchSubject
      .pipe(
        debounceTime(300), // Wait for the user to stop typing
        distinctUntilChanged(), // Avoid duplicate queries
        switchMap((query) => {
          this.isLoading = true;
          this.pagination.page = 0;
          const params = new HttpParams()
            .set("page", 1)
            .set("limit", this.pagination.limit)
            .set("name", query);

          return this._apiServices.get(API_ROUTE.ROLE.getAll, params);
        })
      )
      .subscribe({
        next: (res: any) => {
          this.roles = res.data.roles;
          this.pagination.length = res.data.totalItems;
          this.pagination.limit = res.data.limit;
          this.pagination.page = res.data.currentPage - 1;
          this.dataSource = new MatTableDataSource(this.roles);
          this.dataSource.sortingDataAccessor = (item, property) => {
            if (property === "createdAt" || property === "updatedAt") {
              return new Date(item.metadata[property]).getTime();
            }
            return item[property];
          };
          this.dataSource.sort = this.sort;
          this.isLoading = false;
        },
        error: (err) => {
          this.openSnackBar(err.error.msg);
        },
      });
  }

  ngAfterViewInit(): void {
    if (this.dataSource) {
      setTimeout(() => {
        this.sort.active = "updatedAt";
        this.sort.direction = "desc";
        this.dataSource.sort = this.sort;
      });
    }

    this.sort.sortChange.subscribe(() => {
      this.pagination.page = 0;
      this.initData();
    });
  }

  onSearch(event: KeyboardEvent): void {
    // if (event.key === "Enter") {
    const query = this.searchForm.get("search")?.value.trim() || "";
    this.searchSubject.next(query);
  }
  /**
   * Initialize the data by calling the API
   */
  initData(): void {
    this.isLoading = true;
    const params = new HttpParams()
      .set("page", this.pagination.page + 1) // Backend may use 1-based indexing
      .set("limit", this.pagination.limit);

    this._apiServices.get(API_ROUTE.ROLE.getAll, params).subscribe({
      next: (res: any) => {
        this.roles = res.data.roles;
        this.pagination.length = res.data.totalItems; // Assuming the API sends the total count
        this.pagination.limit = res.data.limit; // Assuming the API sends the total count
        this.pagination.page = res.data.currentPage - 1; // Assuming the API sends the total count
        this.dataSource = new MatTableDataSource(this.roles);
        this.dataSource.sortingDataAccessor = (item, property) => {
          if (property === "createdAt" || property === "updatedAt") {
            return new Date(item.metadata[property]).getTime();
          }
          return item[property];
        };
        this.dataSource.sort = this.sort;
        this.isLoading = false;
      },
      error: (err) => {
        this.openSnackBar(err.error.msg);
      },
    });
  }

  /**
   * Handle pagination changes
   */
  onPaginateChange(event: any): void {
    this.pagination.page = event.pageIndex;
    this.pagination.limit = event.pageSize;
    this.initData();
  }

  /**
   * Create a new role
   */
  createRole(): void {
    if (!this.hasDetailedPerm("Role_Management", "Create", "read")) return;

    const dialogRef = this.dialog.open(RoleFormComponent, {
      width: "60%",
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.initData();
    });
  }

  /**
   * View role details
   */
  viewDetail(role: Role): void {
    let newRole = { ...role };
    newRole.functionalPermission = newRole.functionalPermission.filter(
      (module: any) => role.modularPermission.includes(module.key)
    );
    newRole.modularPermission = newRole.modularPermission?.filter(Boolean);
    const dialogRef = this.dialog.open(RoleDetailComponent, {
      width: "50%",
      data: newRole,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.action === "edit") {
        this.editRole(result.role);
      }
    });
  }

  /**
   * Edit an existing role
   */
  editRole(role: Role): void {
    if (!this.hasDetailedPerm("Role_Management", "Edit", "read")) return;

    const dialogRef = this.dialog.open(RoleFormComponent, {
      width: "50%",
      data: role,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) this.initData();
    });
  }

  /**
   * Delete a role
   */
  deleteRole(role: Role): void {
    if (!this.hasDetailedPerm("Role_Management", "Delete", "read")) return;

    const dialogRef = this.dialog.open(FuseConfirmationDialogComponent, {
      data: {
        title: "Delete Confirmation",
        message: `Are you sure you want to delete the role  ${role.name} ?`,
        icon: "heroicons_outline:exclamation-triangle",
        actions: {
          confirm: { show: true, label: "Delete", color: "warn" },
          cancel: { show: true, label: "Cancel" },
        },
        dismissible: true,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === "confirmed") {
        this._apiServices
          .put(API_ROUTE.ROLE.delete, { id: role._id })
          .pipe(tap(() => this.initData()))
          .subscribe({
            next: () => {
              this.openSnackBar("Role deleted successfully");
            },
            error: (err) => {
              const msg = err?.error?.msg || "Failed to delete role";
              this.openSnackBar(msg);
            },
          });
      }
    });
  }
  openSnackBar(message: string, action: string = "") {
    this._snackBar.open(message, action, {
      horizontalPosition: "end",
      verticalPosition: "top",
      duration: 2000,
      panelClass: "app-snackBar",
    });
  }
}
